// @givmo/app-admin — which portal pages the assistant has a context for, and what it offers on each.
//
// ══ THREE SURFACES, AND WHY NOT EIGHT ══════════════════════════════════════════════════════════
//
// The context chip is a claim: it says the assistant is reading the page you are on. That claim is
// only honest where the assistant actually has grounding for it — the employer tool set reads the
// company's giving figures, its top charities, its profile and its own fundraisers, and nothing else.
// So Dashboard, Reports and Fundraisers carry a chip and prompts; Approvals, Employees, Admin Team,
// Settings and Help carry NEITHER, and the composer there is bare.
//
// ⚠ That is a deliberate absence, not an unfinished list. Putting an "Employees" chip on a page whose
// subject no tool can read would advertise a capability the assistant does not have — the person asks
// about their roster, gets told it cannot see one, and now distrusts the chip everywhere. A bare
// composer on those pages says the true thing: the assistant is here, it just has nothing page-
// specific to offer. The panel itself works identically on every page — one thread, one composer.
//
// Adding a fourth surface means a tool that grounds it, then prompts written for it, in that order.

import { FileText, HeartHandshake, LayoutDashboard } from 'lucide-react';
import type { AssistantSurface } from '@givmo/assistant-panel';

/**
 * The `entry_context.surface` value the BACKEND recognizes for this portal — the THREAD's identity.
 *
 * ⚠ It is what a conversation is CREATED with and what thread adoption matches on, for the whole
 * portal: one persistent thread (D-EP-40) whichever page it was born on. The server uses the surface
 * string to select the tool audience, and it grants nothing on its own: the audience additionally
 * requires the flag pair and the server-injected company-admin membership. The chip/prompt
 * distinction above stays a CLIENT concern; what DOES reach the wire per page is the register
 * below.
 */
export const EMPLOYER_WIRE_SURFACE = 'employer_portal';

/**
 * The surface value stamped on a SEND from a fundraiser page — the manage page and the creation
 * wizard (which has no fundraiser id yet, so the id alone cannot say where the person is).
 *
 * ⚠ THIS IS WHAT PUTS A TURN IN THE SERVER'S DRAFTING REGISTER (D-EP-47). A reply from here is copy
 * the administrator may Insert into a fundraiser description and publish, so the output guard holds
 * it to the §12599.6(f) solicitation classes (no charity a read did not return, no share-of-gift or
 * fee claim, no model-originated relationship, no registration reference, no tax line); a reply from
 * anywhere else in the portal is a recap / Q&A turn and is held only to the tax class and the
 * peer-benchmark quality guard. Same audience, same tools, same consent gate — only the class set
 * differs, and a panel that stopped stamping this would silently relax the guard on every draft.
 */
export const EMPLOYER_FUNDRAISERS_WIRE_SURFACE = 'employer_fundraisers';

/**
 * One host's map from its addresses to the three surfaces, and the address of a fundraiser's own page.
 *
 * ⚠ The pages are the same in both hosts; their ADDRESSES are not (EP-MNT4's address book): the
 * portal's Dashboard is `/dashboard`, the console's Donations module is `/donations` — its ROOT, so it
 * matches EXACTLY or every module page would claim the Dashboard's chip — and the console's
 * fundraisers are the Fundraisers module's own pages at `/fundraisers`.
 */
export interface AssistantSurfaceRoutes {
  /** Route prefixes → the surface a page belongs to. Longest match wins, so a detail route resolves
   *  to its section rather than to whatever happens to be listed first. */
  surfaces: ReadonlyArray<{ prefix: string; id: string; exact?: true }>;
  /** Where a fundraiser's own page lives — its id is the one path segment after this. */
  fundraiserPrefix: string;
}

/** The employer portal's map — the default, exactly the addresses before EP-MNT4. */
export const PORTAL_SURFACE_ROUTES: AssistantSurfaceRoutes = {
  surfaces: [
    { prefix: '/dashboard', id: 'dashboard' },
    { prefix: '/reports', id: 'reports' },
    { prefix: '/company-fundraisers', id: 'fundraisers' },
  ],
  fundraiserPrefix: '/company-fundraisers',
};

/** The company console's map: the Donations module at `/donations`, fundraisers at `/fundraisers`. */
export const DONATIONS_SURFACE_ROUTES: AssistantSurfaceRoutes = {
  surfaces: [
    { prefix: '/donations', id: 'dashboard', exact: true },
    { prefix: '/donations/reports', id: 'reports' },
    { prefix: '/fundraisers', id: 'fundraisers' },
  ],
  fundraiserPrefix: '/fundraisers',
};

/** A fundraiser address segment that is NOT a fundraiser's id: the wizard's, and the shell's own pages
 *  under the console's `/fundraisers` (its Team page and sign-in door). */
const NOT_A_FUNDRAISER = new Set(['new', 'team', 'sign-in']);

/**
 * The three surfaces, with the glyph each one already uses in the sidebar.
 *
 * ⚠ The icon match is load-bearing, not decorative: the chip reads as "the page whose row is
 * highlighted over there" precisely because it carries the same glyph. A different icon would make
 * the chip a second thing to learn.
 */
export const ASSISTANT_SURFACES: readonly AssistantSurface[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    prompts: ['What needs my approval?', 'How are we doing this year?', 'Who joined recently?'],
  },
  {
    id: 'reports',
    label: 'Reports',
    icon: FileText,
    prompts: ['Recap this year', 'Top charities this year', 'Compare to last year'],
  },
  {
    id: 'fundraisers',
    label: 'Fundraisers',
    icon: HeartHandshake,
    prompts: ['Draft a description', 'Write an announcement', 'Draft a thank-you note'],
  },
];

/** The surface id for a pathname, or `''` when the page has none (no chip, no prompts). */
export function surfaceIdForPath(
  pathname: string,
  routes: AssistantSurfaceRoutes = PORTAL_SURFACE_ROUTES,
): string {
  let match = '';
  let matchedLength = 0;
  for (const route of routes.surfaces) {
    const hit =
      pathname === route.prefix ||
      (route.exact !== true && pathname.startsWith(`${route.prefix}/`));
    if (hit && route.prefix.length > matchedLength) {
      match = route.id;
      matchedLength = route.prefix.length;
    }
  }
  return match;
}

/**
 * The surface value stamped on a SEND from `pathname` (the panel's `pageWireSurface`): the fundraiser
 * register on a fundraiser page, the portal's thread surface everywhere else. Creation and thread
 * adoption never use this — see `EMPLOYER_WIRE_SURFACE`.
 */
export function wireSurfaceForPath(
  pathname: string,
  routes: AssistantSurfaceRoutes = PORTAL_SURFACE_ROUTES,
): string {
  return surfaceIdForPath(pathname, routes) === 'fundraisers'
    ? EMPLOYER_FUNDRAISERS_WIRE_SURFACE
    : EMPLOYER_WIRE_SURFACE;
}

/**
 * The fundraiser a turn should be scoped to when the panel is used from a fundraiser's own page, as
 * the context keys the backend reads.
 *
 * Stamped at conversation creation AND on every send (the server reads the send's `client_context`
 * first and falls back to the conversation's `entry_context`), so with one persistent thread
 * (D-EP-40) the id follows the person onto the fundraiser page they are on now and is dropped again
 * when they leave it. See `use-assistant-thread.ts` for why the stamp must replace, never merge.
 */
export function entryContextExtraForPath(
  pathname: string,
  routes: AssistantSurfaceRoutes = PORTAL_SURFACE_ROUTES,
): Record<string, string> {
  const prefix = `${routes.fundraiserPrefix}/`;
  const fundraiserId = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length).split('/', 1)[0]
    : undefined;
  if (fundraiserId === undefined || fundraiserId.length === 0) return {};
  // ⚠ `new` is the WIZARD's route segment, not an id. The draft does not exist yet, so there is
  // nothing to scope to and the backend's own model says the wizard "simply omits it". Stamping the
  // literal is harmless on today's server — the guard fail-closes to an empty allowed-name set on
  // any id it cannot resolve — but it permanently mis-stamps the ONE persistent thread whenever that
  // thread is first created from the wizard, which is exactly where the drafting flow starts.
  // The console's `/fundraisers/team` and `/fundraisers/sign-in` are the shell's pages, never ids.
  if (NOT_A_FUNDRAISER.has(fundraiserId)) return {};
  return { fundraiser_id: fundraiserId };
}

/**
 * The company console's pages, as the `page` key every turn carries (the assistant always knows the
 * page it was asked from — Jonathan, 2026-11-01). Each id is a NAME FROM A CLOSED LIST that the
 * backend maps to its own words for the model (`app/application/assistant/page_context.py`); an id it
 * does not know reads as no page, and no address or record id is ever sent as a page. The record in
 * view travels beside it under its own key (`fundraiser_id`, `entryContextExtraForPath`).
 *
 * ⚠ Keep the two lists in step: a page added here and not there is simply not named to the model.
 */
export const CONSOLE_ASSISTANT_PAGES: ReadonlyArray<{
  prefix: string;
  page: string;
  exact?: true;
}> = [
  { prefix: '/', page: 'apps', exact: true },
  { prefix: '/donations', page: 'donations', exact: true },
  { prefix: '/donations/reports', page: 'donations_reports' },
  { prefix: '/donations/approvals', page: 'donations_approvals' },
  { prefix: '/donations/employees', page: 'donations_employees' },
  { prefix: '/donations/give', page: 'donations_give' },
  { prefix: '/donations/match-program', page: 'donations_match_program' },
  { prefix: '/donations/onboarding', page: 'donations_onboarding' },
  { prefix: '/donations/team', page: 'donations_team' },
  { prefix: '/fundraisers', page: 'fundraisers', exact: true },
  { prefix: '/fundraisers/new', page: 'fundraiser_new' },
  { prefix: '/fundraisers/team', page: 'fundraisers_team' },
  { prefix: '/org', page: 'admin_console' },
  { prefix: '/books', page: 'books' },
  { prefix: '/wallet', page: 'wallet' },
  { prefix: '/documents', page: 'tax_documents' },
  { prefix: '/profile', page: 'profile' },
  { prefix: '/support', page: 'support' },
];

/** The console page a pathname is, from {@link CONSOLE_ASSISTANT_PAGES}: a fundraiser's own page is
 *  `fundraiser`, and an address the list does not name is `other`. Longest match wins. */
export function consolePageForPath(pathname: string): string {
  let page = '';
  let matched = -1;
  for (const entry of CONSOLE_ASSISTANT_PAGES) {
    const hit =
      pathname === entry.prefix ||
      (entry.exact !== true && pathname.startsWith(`${entry.prefix}/`));
    if (hit && entry.prefix.length > matched) {
      page = entry.page;
      matched = entry.prefix.length;
    }
  }
  if (page !== '') return page;
  return Object.keys(entryContextExtraForPath(pathname, DONATIONS_SURFACE_ROUTES)).length > 0
    ? 'fundraiser'
    : 'other';
}
